import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { logAction } from '../lib/audit'

const AuthContext = createContext({})
const TIMEOUT_MS = 15 * 60 * 1000 // 15 minutes

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const timeoutRef = useRef(null)
  const userRef = useRef(null)

  const resetTimer = useCallback(() => {
    if (!userRef.current) return
    clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(async () => {
      await logAction(userRef.current.id, 'session_timeout')
      await supabase.auth.signOut()
    }, TIMEOUT_MS)
  }, [])

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    events.forEach(e => window.addEventListener(e, resetTimer))
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer))
      clearTimeout(timeoutRef.current)
    }
  }, [resetTimer])

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      userRef.current = session?.user ?? null
      if (session?.user) { fetchProfile(session.user.id); resetTimer() }
      else setLoading(false)
    })

    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null)
      userRef.current = session?.user ?? null
      if (session?.user) {
        fetchProfile(session.user.id)
        resetTimer()
        if (event === 'SIGNED_IN') logAction(session.user.id, 'login')
      } else {
        setProfile(null)
        setSubscription(null)
        setLoading(false)
        clearTimeout(timeoutRef.current)
      }
    })

    return () => authSub.unsubscribe()
  }, [])

  async function fetchProfile(userId) {
    const { data: profileData } = await supabase
      .from('users')
      .select('*, clinics(*)')
      .eq('id', userId)
      .single()

    setProfile(profileData)

    if (profileData?.clinic_id) {
      const { data: sub } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('clinic_id', profileData.clinic_id)
        .single()
      setSubscription(sub)
    }

    setLoading(false)
  }

  async function signOut() {
    await logAction(userRef.current?.id, 'logout')
    clearTimeout(timeoutRef.current)
    await supabase.auth.signOut()
  }

  function isSubscriptionActive() {
    if (!subscription) return false
    if (subscription.status === 'active') return true
    if (subscription.status === 'trialing') {
      return new Date(subscription.trial_ends_at) > new Date()
    }
    return false
  }

  function trialDaysLeft() {
    if (!subscription?.trial_ends_at) return 0
    const diff = new Date(subscription.trial_ends_at) - new Date()
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
  }

  return (
    <AuthContext.Provider value={{ user, profile, subscription, loading, isSubscriptionActive, trialDaysLeft, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
